import React from 'react'
import { Typography } from '@material-ui/core'
import {
  css, cx,
} from 'linaria'
import { styled } from 'linaria/react'
import Wysiwyg from '../Wysiwyg/Wysiwyg'
import theme, { COLORS, fontWeights } from '../../styles/theme'
import fontSizes from '../../styles/fontSizes'

const classes = {
  heading: css`
    text-align: center;
    width: 100%;
    margin: 0 auto;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      max-width: ${theme.spacing(87.5)}px;
      margin: 0 auto;
    }

    div {
      margin-top: ${theme.spacing(4)}px;
      max-width: ${theme.spacing(80)}px;
      margin: 0 auto;
    }
  `,
  h1: css`
    & > .MuiTypography-root:first-child {
      font-size: ${fontSizes.h1};
      line-height: 1.15;
      font-weight: ${fontWeights.bold};
    }
    & > h1, & > h2, & > h3, & > h4, & > h5, & > h6 {
      font-size: ${fontSizes.h1};
    }
  `,
  h2: css`
    & > .MuiTypography-root:first-child {
      font-size: ${fontSizes.h2};
      line-height: 1.15;
      font-weight: ${fontWeights.bold};
    }
    & > h1, & > h2, & > h3, & > h4, & > h5, & > h6 {
      font-size: ${fontSizes.h2};
    }
  `,
  h3: css`
    & > .MuiTypography-root:first-child {
      font-size: ${fontSizes.h3};
      line-height: 1.15;
      font-weight: ${fontWeights.bold};
    }
    & > h1, & > h2, & > h3, & > h4, & > h5, & > h6 {
      font-size: ${fontSizes.h3};
    }
  `,
  h4: css`
    & > .MuiTypography-root:first-child {
      font-size: ${fontSizes.h4};
      line-height: 1.15;
      font-weight: ${fontWeights.bold};
    }
    & > h1, & > h2, & > h3, & > h4, & > h5, & > h6 {
      font-size: ${fontSizes.h4};
    }
  `,
  h5: css`
    & > .MuiTypography-root:first-child {
      font-size: ${fontSizes.h5};
      line-height: 1.15;
      font-weight: ${fontWeights.bold};
    }
    & > h1, & > h2, & > h3, & > h4, & > h5, & > h6 {
      font-size: ${fontSizes.h5};
    }
  `,
  h6: css`
    & > .MuiTypography-root:first-child {
      font-size: ${fontSizes.h6};
      line-height: 1.15;
      font-weight: ${fontWeights.bold};
    }
    & > h1, & > h2, & > h3, & > h4, & > h5, & > h6 {
      font-size: ${fontSizes.h6};
    }
  `,
}

export enum Variants {
  h1 = 'h1',
  h2 = 'h2',
  h3 = 'h3',
  h4 = 'h4',
  h5 = 'h5',
  h6 = 'h6',
  body1 = 'body1'
}

// Map variant styles to classes
const variantStyleMap: Record<string, string> = {
  h1: classes.h1,
  h2: classes.h2,
  h3: classes.h3,
  h4: classes.h4,
  h5: classes.h5,
  h6: classes.h6,
}

export interface HeadingProps {
  readonly heading: string;
  readonly variant: Variants;
  readonly subheadline?: string;
  readonly background_color?: string;
  readonly padding_bottom?: number;
  readonly padding_top?: number;
  readonly variant_style?: keyof typeof variantStyleMap;
  readonly text_color?: string;
  readonly text_align?: string;
}

const Background = styled.div<{
  $textColor?: string;
  $backgroundColor?: string;
  $paddingBottom?: number;
  $paddingTop?: number;
  $textAlign?: string;
}>`
  position: relative;
  background-color: ${props =>
    props.$backgroundColor !== undefined
      ? props.$backgroundColor
      : 'initial'};
  padding-bottom: ${props =>
    props.$paddingBottom !== undefined
      ? theme.spacing(props.$paddingBottom) + 'px'
      : theme.spacing(0) + 'px'};
  padding-top: ${props =>
    props.$paddingTop !== undefined
      ? theme.spacing(props.$paddingTop) + 'px'
      : theme.spacing(0) + 'px'};
  text-align: ${props => props.$textAlign || 'center'};
  & > .MuiTypography-root:first-child {
    color: ${props => props.$textColor || COLORS.primary};
  }
  .MuiTypography-root {
    color: ${props => props.$textColor || COLORS.bodyTextPrimary};
  }
`

export default function Heading({
  heading,
  variant,
  variant_style,
  subheadline,
  text_color,
  text_align,
  background_color,
  padding_bottom,
  padding_top
}: HeadingProps) {
  return (
    <Background
      className={cx(classes.heading, variant_style && variantStyleMap[variant_style])}
      $textColor={text_color}
      $backgroundColor={background_color}
      $paddingBottom={padding_bottom}
      $paddingTop={padding_top}
      $textAlign={text_align}
    >
      <Typography variant={variant} color="primary">
        {heading}
      </Typography>
      {subheadline && <Wysiwyg body={subheadline} variant="body1"/>}
    </Background>
  )
}
